import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { apiRequest } from "../utils/ApiRequest";

const UserContext = createContext(null);

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [userLoading, setUserLoading] = useState(true);
  const [hasHydratedUser, setHasHydratedUser] = useState(false);

  useEffect(() => {
    try {
      const storedUser = localStorage.getItem("user");

      if (storedUser) {
        // No local expiry check: whether a login is still valid is decided
        // by the server, and the site signs out when it rejects the token.
        setUser(JSON.parse(storedUser));
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Failed to restore user:", error);
      setUser(null);
      localStorage.removeItem("user");
    } finally {
      setHasHydratedUser(true);
      setUserLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasHydratedUser) return;

    // A browser that blocks storage must not break the app (or a login).
    try {
      if (user) {
        localStorage.setItem("user", JSON.stringify(user));
      } else {
        localStorage.removeItem("user");
      }
    } catch (error) {
      console.error("Could not save login in this browser:", error);
    }
  }, [user, hasHydratedUser]);

  // Signs out this device immediately, then asks the server to end THIS
  // login only (other devices keep their own tokens). If that request fails
  // the customer is still signed out here.
  const signOut = useCallback(async () => {
    const token = user?.token;
    setUser(null);

    if (!token) return;
    try {
      await apiRequest({ script: "customer-logout", data: {}, token });
    } catch (error) {
      console.error("Server sign-out failed (signed out locally):", error);
    }
  }, [user?.token]);

  useEffect(() => {
    if (user) {
      setShowAuthModal(false);
    }
  }, [user]);

  const refreshUser = useCallback(async () => {
    if (!user?.id) return;
    try {
      const data = await apiRequest({ 
        script: "get-customer-profile",
        data: { customer_id: user.id, _t: Date.now() },
        token: user?.token || null
      });
      const profileData = data.user || data.customer;
      if (data.success && profileData) {
        setUser(prev => ({ ...prev, ...profileData }));
      }
    } catch (err) {
      console.error("User refresh failed:", err);
    }
  }, [user?.id, user?.token]);

  const openLoginModal = useCallback(() => {
    if (userLoading || user) return;
    setShowAuthModal(true);
  }, [userLoading, user]);

  const closeLoginModal = useCallback(() => setShowAuthModal(false), []);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        signOut,
        userLoading,
        showAuthModal,
        openLoginModal,
        closeLoginModal,
        refreshUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }

  return context;
};
